/* ==========================================================================
   Sousleau — questionnaire engine.

   Deterministic and inspectable: a result is produced by the first rule whose
   conditions all match the answers actually given. There is no scoring model,
   no inference from a photograph and no automated diagnosis. When nothing
   matches, the "no confident match" outcome is shown rather than a guess.

   Real form controls, one step at a time, answers preserved, back/edit
   available, conditional progress, accessible result. The result is written
   into the page and announced; it is not fetched from anywhere.
   ========================================================================== */

(function () {
  'use strict';

  function t(key) {
    return (window.SousleauT && window.SousleauT(key)) || '';
  }

  /* Locale strings arrive HTML-escaped by Liquid's `t` filter (an apostrophe
     becomes &#39;). Everything here is written with textContent, so the
     entities are decoded once, when the definition is read. */
  const decoder = document.createElement('textarea');
  function decodeStrings(value) {
    if (typeof value === 'string') {
      if (value.indexOf('&') === -1) return value;
      decoder.innerHTML = value;
      return decoder.value;
    }
    if (Array.isArray(value)) return value.map(decodeStrings);
    if (value && typeof value === 'object') {
      Object.keys(value).forEach((k) => {
        value[k] = decodeStrings(value[k]);
      });
    }
    return value;
  }

  class Quiz extends HTMLElement {
    connectedCallback() {
      if (this.ready) {
        if (this.inlineLinkHandler) document.addEventListener('click', this.inlineLinkHandler);
        return;
      }
      const defEl = this.querySelector('[data-quiz-definition]');
      if (!defEl) return;
      try {
        this.def = decodeStrings(JSON.parse(defEl.textContent));
      } catch (e) {
        return;
      }

      this.ready = true;
      this.answers = {};
      this.history = [];
      this.stepsHost = this.querySelector('[data-quiz-steps]');
      this.resultHost = this.querySelector('[data-quiz-result]');
      this.introHost = this.querySelector('[data-quiz-intro]');
      this.progressFill = this.querySelector('[data-quiz-progress-fill]');
      this.progressText = this.querySelector('[data-quiz-progress-text]');
      this.errorEl = this.querySelector('[data-quiz-error]');
      this.alertEl = this.querySelector('[data-quiz-alert]');

      if (this.hasAttribute('data-inline')) {
        this.inlinePanel = this.querySelector('[data-inline-panel]');
        this.inlineBanner = this.querySelector('[data-inline-banner]');
        this.inlineOpener = this.querySelector('[data-inline-open]');
        this.inlineOpener.addEventListener('click', () => this.openInline());
        this.querySelector('[data-inline-close]').addEventListener('click', () => this.closeInline());
        this.addEventListener('keydown', event => {
          if (event.key === 'Escape' && !this.inlinePanel.hidden) this.closeInline();
        });
        this.inlineLinkHandler = event => {
          const link = event.target.closest('a[href]');
          if (!link || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || link.target === '_blank') return;
          const target = new URL(link.href, location.href);
          const current = new URL(location.href);
          if (target.origin === current.origin && target.pathname === current.pathname && target.searchParams.get('view') === current.searchParams.get('view') && target.hash === '#' + this.id) {
            event.preventDefault();
            const drawer = link.closest('details');
            if (drawer) drawer.open = false;
            this.openInline();
          }
        };
        document.addEventListener('click', this.inlineLinkHandler);
        if (location.hash === '#' + this.id) this.openInline();
        return;
      }
      this.initializeFlow();
    }

    disconnectedCallback() {
      if (this.inlineLinkHandler) document.removeEventListener('click', this.inlineLinkHandler);
    }

    openInline() {
      this.inlineBanner.hidden = true;
      this.inlinePanel.hidden = false;
      this.inlineOpener.setAttribute('aria-expanded', 'true');
      if (!this.inlineStarted) {
        this.inlineStarted = true;
        this.initializeFlow();
      }
      const heading = this.inlinePanel.querySelector('[data-quiz-result]:not([hidden]) > h2, .sl-quiz__question');
      if (heading) heading.focus({ preventScroll: true });
      this.scrollIntoView({ behavior: 'auto', block: 'start' });
    }

    closeInline() {
      this.inlinePanel.hidden = true;
      this.inlineBanner.hidden = false;
      this.inlineOpener.setAttribute('aria-expanded', 'false');
      this.inlineOpener.focus({ preventScroll: true });
      this.scrollIntoView({ behavior: 'auto', block: 'start' });
    }

    initializeFlow() {
      // A language switch reloads the document. Restore only complete, valid
      // Thrive choices from this tab, then recalculate with the current rules.
      if (this.def.id === 'thrive') {
        try {
          const saved = JSON.parse(sessionStorage.getItem('sousleau-thrive-result-v8'));
          if (saved && typeof saved === 'object') {
            const valid = {};
            this.def.steps.forEach((step) => {
              if ((step.options || []).some((o) => o.value === saved[step.id])) valid[step.id] = saved[step.id];
            });
            this.answers = valid;
            if (this.visibleSteps().every((step) => valid[step.id] !== undefined)) {
              this.renderResult();
              return;
            }
            this.answers = {};
          }
        } catch (_) { /* Storage may be disabled; the quiz still works. */ }
      }

      const startBtn = this.querySelector('[data-quiz-start]');
      if (this.def.auto_start) {
        this.start();
      } else if (startBtn) {
        startBtn.addEventListener('click', () => this.start());
      } else {
        this.start();
      }
    }

    start() {
      if (this.introHost) this.introHost.hidden = true;
      if (this.resultHost) this.resultHost.hidden = true;
      this.index = 0;
      this.renderStep();
    }

    /* ------------------------------------------------------------ helpers */

    visibleSteps() {
      return this.def.steps.filter((step) => this.stepVisible(step));
    }

    stepVisible(step, answers = this.answers) {
      if (step.showIfAll) return step.showIfAll.every((cond) => this.matches(cond, answers));
      if (!step.showIf) return true;
      return this.matches(step.showIf, answers);
    }

    matches(cond, answers = this.answers) {
      const value = answers[cond.q];
      switch (cond.op) {
        case 'is':
          return value === cond.value;
        case 'not':
          return value !== cond.value;
        case 'in':
          return Array.isArray(cond.value) && cond.value.indexOf(value) !== -1;
        case 'includes':
          return Array.isArray(value) && value.indexOf(cond.value) !== -1;
        case 'includes_any':
          return (
            Array.isArray(value) &&
            Array.isArray(cond.value) &&
            cond.value.some((v) => value.indexOf(v) !== -1)
          );
        case 'count_gte':
          return Array.isArray(value) && value.length >= cond.value;
        case 'count_lte':
          return Array.isArray(value) && value.length <= cond.value;
        case 'gte':
          return typeof value === 'number' && value >= cond.value;
        case 'lte':
          return typeof value === 'number' && value <= cond.value;
        case 'not_lte':
          // True when the value is missing ("not measured") or above the bound.
          return !(typeof value === 'number' && value <= cond.value);
        default:
          return false;
      }
    }

    /* ------------------------------------------------------------ rendering */

    renderStep() {
      if (this.def.id === 'thrive') {
        try { sessionStorage.removeItem('sousleau-thrive-result-v8'); } catch (_) {}
      }
      this.classList.remove('sl-quiz--showing-result');
      const illustrationNote = this.querySelector('.sl-quiz__illustration-note');
      if (illustrationNote) illustrationNote.hidden = false;
      const progress = this.querySelector('.sl-quiz__progress');
      if (progress) progress.hidden = false;
      const steps = this.visibleSteps();
      if (this.index >= steps.length) return this.renderResult();

      const step = steps[this.index];
      if (this.def.id === 'algae') this.querySelectorAll('[data-algae-stage]').forEach((el) => { const active = Number(el.dataset.algaeStage) === (this.index === 0 ? 0 : 1); el.toggleAttribute('data-active', active); if (active) el.setAttribute('aria-current','step'); else el.removeAttribute('aria-current'); });
      const total = steps.length;
      if (this.progressFill) this.progressFill.style.width = ((this.index / total) * 100).toFixed(1) + '%';
      if (this.progressText) {
        this.progressText.textContent = (this.def.adaptive ? t('quiz_choice') : t('quiz_step') || 'Step {current} / {total}')
          .replace(/\{current\}|__current__/, this.index + 1)
          .replace(/\{total\}|__total__/, total);
      }

      const wrap = document.createElement('div');
      wrap.className = 'sl-quiz__step';

      const heading = document.createElement('h2');
      heading.className = 'sl-quiz__question';
      heading.textContent = step.question;
      heading.tabIndex = -1;
      wrap.appendChild(heading);

      if (step.help) {
        const help = document.createElement('p');
        help.className = 'sl-quiz__help';
        help.textContent = step.help;
        wrap.appendChild(help);
      }

      if (step.image) {
        const illustration = document.createElement('img');
        illustration.className = 'sl-quiz__step-image';
        illustration.src = step.image;
        illustration.alt = step.image_alt || '';
        illustration.width = 720;
        illustration.height = 480;
        illustration.decoding = 'async';
        wrap.appendChild(illustration);
      }
      const field = this.buildField(step);
      wrap.appendChild(field);

      const nav = document.createElement('div');
      nav.className = 'sl-quiz__nav';

      if (this.index > 0) {
        const back = document.createElement('button');
        back.type = 'button';
        back.className = 'sl-btn sl-btn--ghost';
        back.textContent = t('quiz_back') || 'Back';
        back.addEventListener('click', () => {
          this.index -= 1;
          this.renderStep();
        });
        nav.appendChild(back);
      }

      const next = document.createElement('button');
      next.type = 'button';
      next.className = 'sl-btn';
      next.textContent =
        this.index === total - 1 ? t('quiz_results') || 'View my results' : (this.def.id === 'algae' ? this.dataset.algaeNext : t('quiz_next')) || 'Next';
      if (this.def.adaptive) {
        const updateNextLabel = () => {
          const pending = this.readField(step, field);
          const preview = { ...this.answers, [step.id]: pending };
          const hasMore = this.def.steps.slice(this.def.steps.indexOf(step) + 1)
            .some((candidate) => this.stepVisible(candidate, preview));
          next.textContent = pending !== null && !hasMore ? t('quiz_results') : t('quiz_next');
        };
        field.addEventListener('change', updateNextLabel);
        updateNextLabel();
      }
      next.addEventListener('click', () => this.advance(step, field));
      nav.appendChild(next);

      wrap.appendChild(nav);

      this.stepsHost.innerHTML = '';
      this.stepsHost.appendChild(wrap);
      if (this.errorEl) this.errorEl.hidden = true;
      heading.focus({ preventScroll: this.def.id === 'algae' && this.index === 0 });
    }

    buildField(step) {
      const existing = this.answers[step.id];
      const fs = document.createElement('fieldset');
      fs.className = 'sl-quiz__options';
      if (step.media) fs.classList.add('sl-quiz__options--grid');

      const legend = document.createElement('legend');
      legend.className = 'sl-visually-hidden';
      legend.textContent = step.question;
      fs.appendChild(legend);

      if (step.type === 'single' || step.type === 'multi') {
        (step.options || []).forEach((option) => {
          const label = document.createElement('label');
          label.className = 'sl-quiz__option';
          const input = document.createElement('input');
          input.type = step.type === 'multi' ? 'checkbox' : 'radio';
          input.name = 'q-' + step.id;
          input.value = option.value;
          if (this.def.id === 'algae' && step.id === 'appearance') input.addEventListener('change', () => { if (input.checked) fs.querySelectorAll('input').forEach((other) => { if (other !== input && (input.value === 'other' || other.value === 'other')) other.checked = false; }); });
          if (step.type === 'multi') {
            input.checked = Array.isArray(existing) && existing.indexOf(option.value) !== -1;
          } else {
            input.checked = existing === option.value;
          }
          const content = document.createElement('span');
          content.className = 'sl-quiz__option-copy';
          const span = document.createElement('strong');
          span.textContent = option.label;
          content.appendChild(span);
          if (option.detail) {
            const detail = document.createElement('small');
            detail.textContent = option.detail;
            content.appendChild(detail);
          }
          if (option.image) {
            label.classList.add('sl-quiz__option--media');
            if (option.scene) label.classList.add('sl-quiz__option--scene');
            if (option.wide) label.classList.add('sl-quiz__option--wide');
            const img = document.createElement('img');
            img.src = option.image;
            img.alt = '';
            img.width = option.scene ? 1536 : 420;
            img.height = option.scene ? 1024 : 420;
            img.loading = 'eager';
            img.decoding = 'async';
            label.appendChild(img);
            const body = document.createElement('span');
            body.className = 'sl-quiz__option-label';
            body.append(input, content);
            label.appendChild(body);
          } else {
            if (step.media) label.classList.add('sl-quiz__option--text');
            label.append(input, content);
          }
          fs.appendChild(label);
        });
      } else if (step.type === 'value') {
        const label = document.createElement('label');
        label.className = 'sl-field';
        const span = document.createElement('span');
        span.className = 'sl-field__label';
        span.textContent = step.unit ? step.question + ' (' + step.unit + ')' : step.question;
        const input = document.createElement('input');
        input.type = 'number';
        input.step = 'any';
        input.min = '0';
        if (this.def.id === 'algae' && step.id === 'light_hours') input.max = '24';
        input.name = 'q-' + step.id;
        if (typeof existing === 'number') input.value = existing;
        label.appendChild(span);
        label.appendChild(input);
        fs.appendChild(label);

        const skip = document.createElement('label');
        skip.className = 'sl-checkbox';
        const skipInput = document.createElement('input');
        skipInput.type = 'checkbox';
        skipInput.name = 'skip-' + step.id;
        skipInput.checked = existing === 'not_measured';
        const skipSpan = document.createElement('span');
        skipSpan.textContent = t('quiz_not_measured') || 'Not measured';
        skip.appendChild(skipInput);
        skip.appendChild(skipSpan);
        fs.appendChild(skip);
      } else if (step.type === 'text') {
        const label = document.createElement('label');
        label.className = 'sl-field';
        const span = document.createElement('span');
        span.className = 'sl-field__label';
        span.textContent = step.question;
        const input = document.createElement('textarea');
        input.name = 'q-' + step.id;
        input.rows = 3;
        if (typeof existing === 'string') input.value = existing;
        label.appendChild(span);
        label.appendChild(input);
        fs.appendChild(label);
      } else if (step.type === 'measurements') {
        (step.fields || []).forEach((name) => {
          const row = document.createElement('div');
          row.className = 'sl-inline';
          row.style.marginBottom = '1rem';

          const label = document.createElement('label');
          label.className = 'sl-field';
          label.style.margin = '0';
          label.style.flex = '1 1 14rem';
          const span = document.createElement('span');
          span.className = 'sl-field__label';
          span.textContent = name;
          const input = document.createElement('input');
          input.type = 'number';
          input.step = 'any';
          input.name = 'm-' + step.id + '-' + name;
          if (existing && existing[name] && existing[name].value !== undefined) {
            input.value = existing[name].value;
          }
          label.appendChild(span);
          label.appendChild(input);

          const unit = document.createElement('label');
          unit.className = 'sl-field';
          unit.style.margin = '0';
          unit.style.flex = '1 1 10rem';
          const unitSpan = document.createElement('span');
          unitSpan.className = 'sl-field__label';
          unitSpan.textContent = 'Unit';
          const unitInput = document.createElement('input');
          unitInput.type = 'text';
          unitInput.name = 'u-' + step.id + '-' + name;
          if (existing && existing[name] && existing[name].unit) unitInput.value = existing[name].unit;
          unit.appendChild(unitSpan);
          unit.appendChild(unitInput);

          row.appendChild(label);
          row.appendChild(unit);
          fs.appendChild(row);
        });
      }

      if (step.free_text) {
        const label = document.createElement('label');
        label.className = 'sl-field';
        label.style.marginTop = '1.6rem';
        const span = document.createElement('span');
        span.className = 'sl-field__label';
        span.textContent = step.help || '';
        const input = document.createElement('input');
        input.type = 'text';
        input.name = 'free-' + step.id;
        if (this.answers[step.id + '_text']) input.value = this.answers[step.id + '_text'];
        label.appendChild(span);
        label.appendChild(input);
        fs.appendChild(label);
      }

      return fs;
    }

    advance(step, field) {
      if (this.def.id === 'algae' && step.id === 'light_hours') {
        const hours = field.querySelector('input[type=number]');
        const unknown = field.querySelector('input[type=checkbox]');
        if (hours && hours.value !== '' && !(unknown && unknown.checked) && !hours.checkValidity()) { hours.reportValidity(); return; }
      }
      const value = this.readField(step, field);
      if (value === null && !step.optional) {
        if (this.errorEl) {
          this.errorEl.textContent =
            step.type === 'value' ? t('quiz_enter_value') : t('quiz_choose_answer');
          this.errorEl.hidden = false;
        }
        return;
      }
      this.answers[step.id] = value;
      this.def.steps.forEach((candidate) => {
        if (!this.stepVisible(candidate)) {
          delete this.answers[candidate.id];
          delete this.answers[candidate.id + '_text'];
        }
      });

      const freeInput = field.querySelector('[name^="free-"]');
      if (freeInput) this.answers[step.id + '_text'] = freeInput.value.trim();

      /* An answer flagged as an alert takes priority over everything else:
         the animals' condition comes first and no product is suggested. */
      if (step.alertOn && value === step.alertOn) {
        this.showAlert(step.alert);
        return;
      }

      this.index += 1;
      this.renderStep();
    }

    readField(step, field) {
      if (step.type === 'single') {
        const checked = field.querySelector('input:checked');
        return checked ? checked.value : null;
      }
      if (step.type === 'multi') {
        const values = Array.prototype.map.call(field.querySelectorAll('input:checked'), (i) => i.value);
        return values.length ? values : null;
      }
      if (step.type === 'value') {
        const skip = field.querySelector('[name^="skip-"]');
        if (skip && skip.checked) return 'not_measured';
        const input = field.querySelector('input[type="number"]');
        if (!input || input.value === '') return null;
        const num = parseFloat(input.value);
        return isNaN(num) ? null : num;
      }
      if (step.type === 'text') {
        const input = field.querySelector('textarea');
        const text = input ? input.value.trim() : '';
        return text === '' ? null : text;
      }
      if (step.type === 'measurements') {
        const out = {};
        (step.fields || []).forEach((name) => {
          const v = field.querySelector('[name="m-' + step.id + '-' + name + '"]');
          const u = field.querySelector('[name="u-' + step.id + '-' + name + '"]');
          if (v && v.value !== '') {
            out[name] = { value: parseFloat(v.value), unit: u ? u.value.trim() : '' };
          }
        });
        return Object.keys(out).length ? out : null;
      }
      return null;
    }

    showAlert(message) {
      if (this.alertEl) {
        this.alertEl.textContent = message;
        this.alertEl.hidden = false;
        this.alertEl.focus();
      }
      this.stepsHost.innerHTML = '';
      const restart = document.createElement('button');
      restart.type = 'button';
      restart.className = 'sl-btn sl-btn--ghost';
      restart.textContent = t('quiz_restart') || 'Start again';
      restart.addEventListener('click', () => {
        this.answers = {};
        if (this.alertEl) this.alertEl.hidden = true;
        this.start();
      });
      this.stepsHost.appendChild(restart);
    }

    /* -------------------------------------------------------------- result */

    /* Returns { rule, suppressed }.

       A rule the project has not validated against the real product labels
       never produces a public recommendation: it is skipped, and the
       questionnaire falls through to the "no confident match" outcome. The
       skip is recorded so the page can say that a match existed but was not
       confident enough to act on, instead of implying nothing matched.

       The merchant can enable unvalidated rules deliberately through the
       theme setting, once the mapping has been checked. */
    evaluate() {
      const allowUnvalidated = this.def.allow_unvalidated === true;
      let suppressed = false;

      for (const rule of this.def.rules || []) {
        if (!(rule.when || []).every((cond) => this.matches(cond))) continue;
        if (rule.needs_validation && !allowUnvalidated) {
          suppressed = true;
          continue;
        }
        return { rule: rule, suppressed: suppressed };
      }
      return { rule: null, suppressed: suppressed };
    }

    renderResult() {
      if (this.def.id === 'thrive') {
        try { sessionStorage.setItem('sousleau-thrive-result-v8', JSON.stringify(this.answers)); } catch (_) {}
      }
      this.classList.add('sl-quiz--showing-result');
      const illustrationNote = this.querySelector('.sl-quiz__illustration-note');
      if (illustrationNote) illustrationNote.hidden = true;
      const outcome = this.evaluate();
      const rule = outcome.rule;
      const id = rule ? rule.result : 'no_match';

      if (this.progressFill) this.progressFill.style.width = '100%';
      const progress = this.querySelector('.sl-quiz__progress');
      if (progress && this.def.adaptive) progress.hidden = true;
      const phNote = this.querySelector('[data-thrive-ph-note]');
      if (phNote) phNote.hidden = !(id === 'og' && this.answers.profile === 'advanced');

      this.stepsHost.innerHTML = '';

      const capsComplement = this.def.id === 'thrive' && this.answers.roots === 'yes' &&
        ['c', 'og', 'plus', 'shrimp'].includes(id);
      const recommendations = this.querySelector('.sl-quiz__recommendations');
      if (recommendations) recommendations.classList.toggle('sl-quiz__recommendations--pair', capsComplement);
      this.querySelectorAll('[data-caps-standalone]').forEach((node) => { node.hidden = capsComplement; });
      this.querySelectorAll('[data-caps-complement]').forEach((node) => { node.hidden = !capsComplement; });
      const rootUncertain = this.querySelector('[data-root-uncertain]');
      if (rootUncertain) rootUncertain.hidden = !(this.answers.roots === 'unknown' && ['c','og','plus','shrimp'].includes(id));
      const algaeResults = this.def.id === 'algae' ? (this.def.rules || []).filter((r) => !r.needs_validation && (r.when || []).every((c) => this.matches(c))).map((r) => r.result) : [];
      if (this.def.id === 'algae') {
        this.querySelectorAll('[data-algae-stage]').forEach((el) => { const active = el.dataset.algaeStage === '2'; el.toggleAttribute('data-active',active); if (active) el.setAttribute('aria-current','step'); else el.removeAttribute('aria-current'); });
        const long = this.querySelector('[data-algae-long]'); if (long) long.hidden = !(typeof this.answers.light_hours === 'number' && this.answers.light_hours > 8);
      }
      const panels = this.querySelectorAll('[data-result-id]');
      let shown = false;
      panels.forEach((panel) => {
        const match = algaeResults.includes(panel.getAttribute('data-result-id')) || panel.getAttribute('data-result-id') === id ||
          (capsComplement && panel.getAttribute('data-result-id') === 'caps');
        panel.hidden = !match;
        if (match) {
          shown = true;
          // Start result illustrations as soon as the recommendation appears,
          // including sections below the fold, without loading every outcome.
          panel.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
        }
      });

      const fallback = this.querySelector('[data-result-fallback]');
      if (fallback) fallback.hidden = shown;

      /* The list of answers that produced this result, so the visitor can see
         why — and correct anything that is wrong. */
      const why = this.querySelector('[data-result-answers]');
      if (why) {
        why.innerHTML = '';
        const steps = this.visibleSteps();
        steps.forEach((step) => {
          const value = this.answers[step.id];
          if (value === undefined || value === null) return;
          const li = document.createElement('li');
          li.textContent = step.question + ' — ' + this.describe(step, value);
          why.appendChild(li);
        });
      }

      const validation = this.querySelector('[data-result-validation]');
      if (validation) validation.hidden = !(rule && rule.needs_validation);

      /* Shown only when a rule did match but was withheld for lack of
         validation, so the visitor is told why they are seeing the careful
         answer rather than a recommendation. */
      const suppressedNote = this.querySelector('[data-result-suppressed]');
      if (suppressedNote) suppressedNote.hidden = !(outcome.suppressed && !rule);

      if (this.resultHost) {
        this.resultHost.hidden = false;
        const h = this.resultHost.querySelector('h2');
        if (h) {
          h.tabIndex = -1;
          h.focus();
        }
      }

      const restart = this.querySelector('[data-quiz-restart]');
      if (restart) {
        restart.onclick = () => {
          this.answers = {};
          this.start();
        };
      }
      this.querySelectorAll('[data-quiz-edit]').forEach((edit) => {
        edit.onclick = () => {
          if (this.resultHost) this.resultHost.hidden = true;
          this.index = this.def.id === 'thrive' ? 0 : Math.max(0, this.visibleSteps().length - 1);
          this.renderStep();
        };
      });
    }

    describe(step, value) {
      if (Array.isArray(value)) {
        return value
          .map((v) => {
            const option = (step.options || []).find((o) => o.value === v);
            return option ? option.label : v;
          })
          .join(', ');
      }
      if (value === 'not_measured') return t('quiz_not_measured') || 'Not measured';
      if (typeof value === 'object') {
        return Object.keys(value)
          .map((k) => k + ' ' + value[k].value + (value[k].unit ? ' ' + value[k].unit : ''))
          .join(' · ');
      }
      const option = (step.options || []).find((o) => o.value === value);
      return option ? option.label : String(value);
    }
  }

  if (!customElements.get('sl-quiz')) customElements.define('sl-quiz', Quiz);
})();
